import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const CalendarView = ({ occasions, selectedDate, onDateSelect, onOccasionClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getOccasionsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return occasions.filter(occasion => occasion.date === dateStr);
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === currentMonth.getMonth() && 
           today.getFullYear() === currentMonth.getFullYear();
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return selectedDate === dateStr;
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 lg:h-32"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOccasions = getOccasionsForDate(day);
      const hasOccasions = dayOccasions.length > 0;
      
      days.push(
        <div
          key={day}
          className={`h-24 lg:h-32 border border-accent-100 p-2 cursor-pointer transition-all duration-200 hover:bg-surface ${
            isToday(day) ? 'bg-primary-50 border-primary-200' : ''
          } ${isSelected(day) ? 'bg-primary-100 border-primary-300' : ''}`}
          onClick={() => onDateSelect(`${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)}
        >
          <div className={`text-sm font-medium mb-1 ${
            isToday(day) ? 'text-primary-700' : 'text-accent-700'
          }`}>
            {day}
          </div>
          
          {hasOccasions && (
            <div className="space-y-1">
              {dayOccasions.slice(0, 2).map((occasion, index) => (
                <div
                  key={index}
                  className={`text-xs px-2 py-1 rounded-md truncate cursor-pointer transition-colors duration-200 ${
                    occasion.category === 'birthday' ? 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200' :
                    occasion.category === 'anniversary' ? 'bg-primary-100 text-primary-700 hover:bg-primary-200' :
                    occasion.category === 'holiday'? 'bg-warning-100 text-warning-700 hover:bg-warning-200' : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOccasionClick(occasion);
                  }}
                >
                  {occasion.title}
                </div>
              ))}
              {dayOccasions.length > 2 && (
                <div className="text-xs text-accent-500 font-medium">
                  +{dayOccasions.length - 2} more
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden">
      {/* Calendar Header */}
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-6 py-4 border-b border-accent-100">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-accent-800">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="ChevronLeft"
              onClick={() => navigateMonth(-1)}
              className="text-accent-600 hover:text-accent-800"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
              className="text-accent-600 hover:text-accent-800 px-3"
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="ChevronRight"
              onClick={() => navigateMonth(1)}
              className="text-accent-600 hover:text-accent-800"
            />
          </div>
        </div>
      </div>

      {/* Days of Week Header */}
      <div className="grid grid-cols-7 bg-surface border-b border-accent-100">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="p-3 text-center text-sm font-medium text-accent-600 border-r border-accent-100 last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {renderCalendarDays()}
      </div>
    </div>
  );
};

export default CalendarView;